import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";

const PublicChatPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const r = await publicApi.get(`/public/chat/${token}`);
      setData(r.data);
    } catch {
      setErr("Chat link is invalid.");
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [token]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data?.messages?.length]);

  const send = async () => {
    const txt = input.trim();
    if (!txt || sending) return;
    setSending(true);
    try {
      await publicApi.post(`/public/chat/${token}/messages`, { body: txt });
      setInput("");
      await load();
    } catch {
      toast.error("Send failed");
    } finally {
      setSending(false);
    }
  };

  if (err)
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="card-flat max-w-md text-center">
          <p className="label-eyebrow">Error</p>
          <h1 className="font-serif text-3xl text-[#112018] mt-2">Chat Link Invalid</h1>
        </div>
      </div>
    );
  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center label-eyebrow">
        Loading…
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F7F6F2] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#1A2F24] flex items-center justify-center">
            <span className="text-[#F7F6F2] font-serif text-xl">L</span>
          </div>
          <div>
            <p className="font-serif text-xl text-[#112018] leading-none">{data.firm_name}</p>
            <p className="label-eyebrow leading-none mt-1">
              Direct message · {data.primary_contact}
            </p>
          </div>
        </div>

        <div className="card-flat p-0 flex flex-col" style={{ minHeight: "70vh" }}>
          <div className="px-6 py-4 border-b border-[#1A2F24]/12">
            <p className="label-eyebrow">Conversation</p>
            <p className="font-serif text-2xl text-[#112018]">{data.title}</p>
          </div>
          <div
            ref={scrollRef}
            className="flex-1 p-6 space-y-3 overflow-y-auto"
            style={{ maxHeight: "55vh" }}
            data-testid="public-chat-thread"
          >
            {data.messages.length === 0 && (
              <p className="text-center text-sm text-[#606C64] italic py-12">
                No messages yet. Say hello below.
              </p>
            )}
            {data.messages.map((m) => (
              <div
                key={m.id}
                className={m.sender_kind === "client" ? "chat-bubble-user" : "chat-bubble-ai"}
                data-testid={`public-msg-${m.sender_kind}`}
              >
                <p className="text-xs opacity-70 mb-1 font-mono">{m.sender_name}</p>
                <p>{m.body}</p>
                <p className="text-[10px] opacity-60 mt-1 font-mono">
                  {new Date(m.at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
          <div className="border-t border-[#1A2F24]/12 p-3 flex gap-2">
            <input
              data-testid="public-chat-input"
              className="input-clean flex-1"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={`Reply to ${data.firm_name}…`}
            />
            <button
              onClick={send}
              disabled={sending}
              className="btn-primary px-4"
              data-testid="public-chat-send"
            >
              <PaperPlaneTilt size={16} weight="fill" />
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-[#606C64] mt-6 label-eyebrow">
          Encrypted client chat · {data.firm_name}
        </p>
      </div>
    </div>
  );
};

export default PublicChatPage;
